import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { useGetMeQuery } from 'store/services/user/meApi';
import { meApi } from 'store/services/user/meApi';
import { useCreateCheckoutSessionMutation } from 'store/services/subscription/subscriptionApi';
import { AssetUrls } from 'config/assets';
import Points from 'components/Points';
import { MONTHLY_MAINTENANCE_FEE_POINTS } from 'config/fees';
import { toast } from 'react-toastify';
import { useConfetti } from 'hooks/useConfetti';
import BorderColorPickerModal from 'components/BorderColorPickerModal/BorderColorPickerModal';
import BubbleColorPickerModal from 'components/BubbleColorPickerModal/BubbleColorPickerModal';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import {
  AnimatedBorderCard,
  EliteBadgeCard,
  TrophyCaseCard,
  ChatCustomizationCard,
  CustomUrlCard,
  SocialLinksCard,
} from 'screens/Public/Elite/FeatureCards';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import 'screens/Public/Elite/elite.css';
import './eternal-durdles.css';

const BNG_47_SCRYFALL_ID = '546dae51-070c-4f3f-b0b4-6b88ff314af1';

const ED_LOGO_URL = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/images/ED25LOGO.png';
const ED_ZAC_URL = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/images/Zac+THUMBS.webp';
const ED_PHIL_URL = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/images/Phil+Thumb.webp';

const PROMO_SOURCE = 'eternaldurdles';

type FeatureRow = {
  label: string;
  free: React.ReactNode;
  elite: React.ReactNode;
  anchor?: string;
};

const SHARED_FEATURES: FeatureRow[] = [
  { label: 'Unlimited trading', free: '✓', elite: '✓' },
  { label: 'Have and want list management', free: '✓', elite: '✓' },
  { label: 'Bulk CSV import', free: '✓', elite: '✓' },
  { label: 'Auto-match engine', free: '✓', elite: '✓' },
];

const ELITE_SPECIFIC_FEATURES: FeatureRow[] = [
  {
    label: 'Monthly maintenance fee',
    free: <Points points={MONTHLY_MAINTENANCE_FEE_POINTS} type="small" />,
    elite: <span className="elite-compare-feature-row__waived">Waived</span>,
  },
  { label: 'Badge display', free: '1', elite: 'Up to 4', anchor: 'feature-elite-badge' },
  { label: 'Animated avatar border', free: '—', elite: '✓', anchor: 'feature-animated-border' },
  { label: 'Custom border colors', free: '—', elite: '✓', anchor: 'feature-animated-border' },
  { label: 'Chat bubble customization', free: '—', elite: '✓', anchor: 'feature-chat-customization' },
  { label: 'Custom profile slug', free: '—', elite: '✓', anchor: 'feature-custom-url' },
  { label: 'Custom emoji reactions', free: '—', elite: '✓', anchor: 'feature-chat-customization' },
  { label: 'Social links on profile', free: '—', elite: '✓', anchor: 'feature-social-links' },
  { label: 'Prefer domestic trades', free: '—', elite: '✓' },
  { label: 'Zero ads', free: '—', elite: '✓' },
];

const FEATURES: FeatureRow[] = [...SHARED_FEATURES, ...ELITE_SPECIFIC_FEATURES];

const FREE_FEATURES = FEATURES.filter((f) => f.free !== '—');

const formatTrialEndDate = (days: number): string => {
  const d = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const EternalDurdlesPage: React.FC = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [searchParams] = useSearchParams();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showBorderColorModal, setShowBorderColorModal] = useState(false);
  const [showBubbleColorModal, setShowBubbleColorModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationVisible, setCelebrationVisible] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const celebrationFired = useRef(false);
  const autoCheckoutFired = useRef(false);

  const { triggerEliteCelebration } = useConfetti();

  const isSuccess = searchParams.get('success') === 'true';
  const isCanceled = searchParams.get('canceled') === 'true';
  const autoCheckout = searchParams.get('autocheckout') === '1';
  const intervalParam = searchParams.get('interval');

  const { data: meData } = useGetMeQuery(undefined, {
    skip: !currentUser,
  });

  const isElite = meData?.isElite || false;

  const [createCheckout, { isLoading: isCheckoutLoading }] = useCreateCheckoutSessionMutation();
  const navigate = useNavigate();

  // Sync billing interval from the URL param when returning from signup auto-checkout
  useEffect(() => {
    if (autoCheckout && (intervalParam === 'monthly' || intervalParam === 'annual')) {
      setBillingInterval(intervalParam);
    }
  }, [autoCheckout, intervalParam]);

  // Refresh user data after successful checkout
  useEffect(() => {
    if (isSuccess) {
      dispatch(meApi.util.invalidateTags(['User']));
    }
  }, [isSuccess, dispatch]);

  // Celebration sequence on successful checkout
  useEffect(() => {
    if (isSuccess && !celebrationFired.current) {
      celebrationFired.current = true;

      // Remove ?success=true from URL immediately
      window.history.replaceState({}, '', window.location.pathname);

      // Show celebration overlay
      setShowCelebration(true);
      requestAnimationFrame(() => {
        setCelebrationVisible(true);
      });

      triggerEliteCelebration();

      const timer = setTimeout(() => {
        setCelebrationVisible(false);
        setTimeout(() => setShowCelebration(false), 600);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [isSuccess, triggerEliteCelebration]);

  const dismissCelebration = useCallback(() => {
    setCelebrationVisible(false);
    setTimeout(() => setShowCelebration(false), 600);
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  const handleSubscribe = useCallback(
    async (source: string, intervalOverride?: 'monthly' | 'annual') => {
      const effectiveInterval = intervalOverride ?? billingInterval;

      if (!currentUser) {
        ImportSessionStorage.setReturnTo(
          `/eternaldurdles?autocheckout=1&interval=${effectiveInterval}`,
        );
        navigate('/welcome');
        return;
      }

      try {
        const result = await createCheckout({
          source,
          billingInterval: effectiveInterval,
          promoSource: PROMO_SOURCE,
        }).unwrap();
        window.location.href = result.url;
      } catch {
        toast.error('Failed to start checkout. Please try again.');
      }
    },
    [currentUser, billingInterval, createCheckout, navigate],
  );

  // Auto-advance to checkout after returning from signup
  useEffect(() => {
    if (
      autoCheckout &&
      currentUser &&
      !isElite &&
      !autoCheckoutFired.current &&
      !isCheckoutLoading
    ) {
      autoCheckoutFired.current = true;

      const resolvedInterval: 'monthly' | 'annual' =
        intervalParam === 'annual' ? 'annual' : 'monthly';

      // Strip the one-time params from the URL
      const url = new URL(window.location.href);
      url.searchParams.delete('autocheckout');
      url.searchParams.delete('interval');
      window.history.replaceState({}, '', url.pathname + (url.search || ''));

      handleSubscribe('eternaldurdles-auto', resolvedInterval);
    }
  }, [autoCheckout, currentUser, isElite, isCheckoutLoading, intervalParam, handleSubscribe]);

  const heroBgUrl = AssetUrls.cards.getCardImage(BNG_47_SCRYFALL_ID, 'art_crop');

  const trialDays = billingInterval === 'monthly' ? 30 : 90;
  const trialEndDate = formatTrialEndDate(trialDays);
  const trialAmountLabel = billingInterval === 'monthly' ? '$5.99' : '$47.88';
  const trialCtaLabel =
    billingInterval === 'monthly' ? 'Claim Your Free Month' : 'Claim Your 3 Free Months';
  const trialPriceMain =
    billingInterval === 'monthly' ? 'First Month Free' : '3 Months Free';
  const trialPriceSub =
    billingInterval === 'monthly' ? 'Then $5.99/month' : 'Then $47.88/year';
  const finePrint = `No charge today. Your first payment of ${trialAmountLabel} will be on ${trialEndDate}. Cancel anytime before then.`;

  const profileHref = meData?.customSlug
    ? `/u/${meData.customSlug}`
    : meData?.username
      ? `/profile/u/${meData.username}`
      : '/home';

  const renderFeatureCards = (elite: boolean) => (
    <div className="elite-feature-cards-list">
      <div id="feature-animated-border">
        <AnimatedBorderCard
          isElite={elite}
          avatarUrl={meData?.avatarUrl}
          username={meData?.username}
          borderColor1={meData?.borderColor1}
          borderColor2={meData?.borderColor2}
          onOpenBorderColorModal={() => setShowBorderColorModal(true)}
        />
      </div>
      <div id="feature-chat-customization">
        <ChatCustomizationCard
          isElite={elite}
          bubbleColor1={meData?.bubbleColor1}
          bubbleColor2={meData?.bubbleColor2}
          onOpenBubbleColorModal={() => setShowBubbleColorModal(true)}
        />
      </div>
      <div id="feature-trophy-case">
        <TrophyCaseCard isElite={elite} username={meData?.username} />
      </div>
      <div id="feature-elite-badge">
        <EliteBadgeCard isElite={elite} />
      </div>
      <div id="feature-custom-url">
        <CustomUrlCard
          isElite={elite}
          customSlug={meData?.customSlug}
          customSlugSetAt={meData?.customSlugSetAt}
        />
      </div>
      <div id="feature-social-links">
        <SocialLinksCard isElite={elite} username={meData?.username} />
      </div>
    </div>
  );

  const scrollToFeature = (anchorId: string) => {
    document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderAlreadyElite = () => (
    <div className="ed-already-elite">
      <img
        src={AssetUrls.icons.elite.badge}
        alt="Elite Badge"
        className="ed-already-elite__badge"
      />
      <h2 className="ed-already-elite__title">
        You&apos;re already an Elite member. Thanks for your support! 🎴
      </h2>
      <p className="ed-already-elite__body">
        This trial offer is for new Elite subscribers, but you&apos;re already part of the club.
      </p>
      <button className="ed-already-elite__cta" onClick={() => navigate(profileHref)}>
        Go to your profile
      </button>
    </div>
  );

  const renderFeatureShowcase = () => (
    <div className="elite-showcase-section">
      <section className="eternal-durdles-hero">
        <div className="ed-hero__cobrand">
          <img
            className="ed-cobrand__wordmark"
            src={AssetUrls.logos.arevakhachIcon}
            alt="TradeMagic"
            loading="eager"
          />
          <span className="ed-hero__divider" aria-hidden="true">
            ×
          </span>
          <img
            className="ed-cobrand__logo"
            src={ED_LOGO_URL}
            alt="Eternal Durdles podcast logo"
            loading="eager"
          />
        </div>
        <div className="ed-hero__welcome">
          <h1 className="ed-hero__headline">Welcome, Durdlers!</h1>
          <p className="ed-hero__subhead">
            Heard us on the show? Claim a free trial of TradeMagic Elite — one month free on the
            monthly plan, or three months free on the annual plan. No charge today, cancel
            anytime.
          </p>
        </div>
      </section>

      <div className="elite-billing-toggle" aria-label="Billing interval">
        <span
          className={`elite-billing-toggle__label ${billingInterval === 'monthly' ? 'elite-billing-toggle__label--active' : ''}`}
        >
          Monthly
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={billingInterval === 'annual'}
          aria-label="Toggle yearly billing"
          className={`elite-billing-switch ${billingInterval === 'annual' ? 'elite-billing-switch--on' : ''}`}
          onClick={() => setBillingInterval(billingInterval === 'annual' ? 'monthly' : 'annual')}
        >
          <span className="elite-billing-switch__thumb" />
        </button>
        <span
          className={`elite-billing-toggle__label ${billingInterval === 'annual' ? 'elite-billing-toggle__label--active' : ''}`}
        >
          Yearly
        </span>
        <span
          className={`elite-billing-toggle__savings ${billingInterval === 'annual' ? 'elite-billing-toggle__savings--visible' : ''}`}
        >
          Save 33%
        </span>
      </div>

      <div className="ed-pricing-wrapper">
        <img
          className="ed-portrait ed-portrait--zac"
          src={ED_ZAC_URL}
          alt="Zac from the Eternal Durdles podcast"
          loading="lazy"
        />

        <div className="elite-compare-grid">
          <div className="elite-compare-card elite-compare-card--free">
            <div className="elite-compare-card__header">
              <h2 className="elite-compare-card__label">Free</h2>
              <div className="elite-compare-card__price">
                <span className="elite-compare-card__price-main">$0.00</span>
                <span className="elite-compare-card__price-sub">/ forever</span>
              </div>
            </div>
            <ul className="elite-compare-features">
              {FREE_FEATURES.map((f) => (
                <li key={`free-${f.label}`} className="elite-compare-feature-row">
                  <span className="elite-compare-feature-row__label">{f.label}</span>
                  <span className="elite-compare-feature-row__value">{f.free}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="elite-compare-card elite-compare-card--elite elite-compare-card--variant-a">
            <div className="elite-compare-card__header">
              <div className="elite-compare-card__label-row">
                <img src={AssetUrls.icons.elite.badge} alt="" className="elite-compare-card__badge" />
                <h2 className="elite-compare-card__label">Elite</h2>
              </div>
              <div className="elite-compare-card__price">
                <span className="elite-compare-card__price-main ed-trial-headline">
                  {trialPriceMain}
                </span>
                <span className="elite-compare-card__price-sub ed-trial-subline">
                  {trialPriceSub}
                </span>
              </div>
            </div>
            <button
              className="elite-subscribe-button elite-subscribe-button--shimmer"
              onClick={() => handleSubscribe('eternaldurdles-top')}
              disabled={isCheckoutLoading}
            >
              {isCheckoutLoading ? 'Loading...' : trialCtaLabel}
            </button>
            <p className="elite-price-note ed-trial-fineprint">{finePrint}</p>
            <p className="elite-price-note">Everything in Free, plus:</p>
            <ul className="elite-compare-features">
              {ELITE_SPECIFIC_FEATURES.map((f) => {
                const isAnchorable = f.anchor && f.elite !== '—';
                return (
                  <li key={`elite-${f.label}`} className="elite-compare-feature-row">
                    {isAnchorable ? (
                      <button
                        type="button"
                        className="elite-compare-feature-row__label elite-compare-feature-row__label--anchor"
                        onClick={() => scrollToFeature(f.anchor as string)}
                      >
                        {f.label}
                      </button>
                    ) : (
                      <span className="elite-compare-feature-row__label">{f.label}</span>
                    )}
                    <span className="elite-compare-feature-row__value">{f.elite}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <img
          className="ed-portrait ed-portrait--phil"
          src={ED_PHIL_URL}
          alt="Phil from the Eternal Durdles podcast"
          loading="lazy"
        />
      </div>

      {renderFeatureCards(false)}

      <div className="elite-free-section">
        <h2 className="elite-free-heading">Trading is free. Always.</h2>
        <p className="elite-free-body">
          A trading platform only works when everyone can trade. No paid matchmaking, no trade
          slots, no points-for-cash. Elite is how you support the platform and make your account
          your own.
        </p>
        <button
          className="elite-subscribe-button elite-subscribe-button--shimmer"
          onClick={() => handleSubscribe('eternaldurdles-bottom')}
          disabled={isCheckoutLoading}
        >
          {isCheckoutLoading ? 'Loading...' : trialCtaLabel}
        </button>
      </div>

      {isCanceled && (
        <p className="elite-canceled-note">
          Checkout was canceled. No charges were made. Feel free to claim your trial whenever
          you&apos;re ready.
        </p>
      )}
    </div>
  );

  return (
    <>
      <SEOHelmet
        title="Eternal Durdles x TradeMagic - Claim Your Free Elite Trial"
        description="Listeners of the Eternal Durdles podcast get a free trial of TradeMagic Elite: one month free on monthly or three months free on annual. Cancel anytime."
        canonical="https://trademagic.gg/eternaldurdles"
        keywords="Eternal Durdles, TradeMagic Elite, MTG podcast partner, free Elite trial, Magic card trading"
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Eternal Durdles', url: 'https://trademagic.gg/eternaldurdles' },
        ]}
      />

      <div className="elite-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="elite-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="elite-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/eternaldurdles', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div
            className="elite-main elite-main--showcase"
            style={{ '--elite-hero-bg': `url(${heroBgUrl})` } as React.CSSProperties}
          >
            <TopNavigationBar
              title="Elite"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="elite-container">
              {isElite && !showCelebration
                ? renderAlreadyElite()
                : renderFeatureShowcase()}
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>

      <BorderColorPickerModal
        isOpen={showBorderColorModal}
        onClose={() => setShowBorderColorModal(false)}
        currentColor1={meData?.borderColor1 || '#FFD700'}
        currentColor2={meData?.borderColor2 || '#9B59B6'}
        avatarUrl={meData?.avatarUrl}
        username={meData?.username}
        onSaveOverride={!isElite ? () => handleSubscribe('eternaldurdles-border') : undefined}
      />

      <BubbleColorPickerModal
        isOpen={showBubbleColorModal}
        onClose={() => setShowBubbleColorModal(false)}
        currentColor1={meData?.bubbleColor1 || '#8B5CF6'}
        currentColor2={meData?.bubbleColor2 || '#6D28D9'}
        onSaveOverride={!isElite ? () => handleSubscribe('eternaldurdles-bubble') : undefined}
      />

      {showCelebration && (
        <div
          className={`elite-celebration-overlay ${celebrationVisible ? 'elite-celebration-overlay--visible' : ''}`}
          onClick={dismissCelebration}
        >
          <div className="elite-celebration-content">
            <EliteBadge isElite size={64} />
            <h1 className="elite-celebration-title">You Are Elite</h1>
            <p className="elite-celebration-subtitle">
              Welcome to the club. Your cosmetic features are now active.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default EternalDurdlesPage;
